import { IconAlertCircle, IconCircleCheck, IconClock, IconLoop, IconShield } from '@developerz/ui';
import { type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import type { DispatchOutcome } from '../lib/task-dispatch';
import { NewTaskRefused } from './NewTaskRefused';
import { Button } from './ui/Button';

// What the dispatcher actually did (#1034). Presenter — six verdicts, six
// readings, because five of them are 2xx and only ONE is a dispatch: a queued
// ask waits on capacity, an escalated one produced no task at all, a duped one
// was dropped onto work already in motion, and an `unclear` 2xx named nothing
// at all. A shared "Done ✓" over them would be a lie about where the work went.
//
// The sixth — every non-2xx — is `<NewTaskRefused/>`: the only branch that
// reads the problem body's extension members, so it changes on its own.

export type NewTaskResultProps = {
  readonly outcome: DispatchOutcome;
  /** The repo the ask named — the only way to build a link to a duped PR. */
  readonly repoFullName: string;
  readonly onOpenTask: (taskId: string) => void;
  /** Back to an empty form, same modal. */
  readonly onAgain: () => void;
  readonly onClose: () => void;
};

export function NewTaskResult(props: NewTaskResultProps): JSX.Element {
  // why the cast: `kind === K` narrows the union at runtime, but TS can't carry
  // that narrowing through a generic type parameter.
  const of = <K extends DispatchOutcome['kind']>(
    kind: K,
  ): Extract<DispatchOutcome, { kind: K }> | null =>
    props.outcome.kind === kind ? (props.outcome as Extract<DispatchOutcome, { kind: K }>) : null;

  /** The task worth opening: the one dispatched, queued, or already covering it. */
  const openId = (): string | null =>
    of('dispatched')?.task_id ?? of('queued')?.task_id ?? of('dupe')?.dupe_of_task_id ?? null;

  return (
    <>
      <Switch>
        <Match when={of('dispatched')}>
          {(o) => (
            <div class="alert alert--icon" role="alert">
              <IconCircleCheck />
              <p class="alert__title">{t('fleet.new_task.result_dispatched')}</p>
              <p class="alert__desc">{t('fleet.new_task.result_dispatched_desc')}</p>
              <Show when={o().url}>
                {(url) => (
                  <a class="alert__action" href={url()} target="_blank" rel="noreferrer">
                    {t('fleet.new_task.result_sink_link')}
                  </a>
                )}
              </Show>
            </div>
          )}
        </Match>

        <Match when={of('queued')}>
          {(o) => (
            <div class="alert alert--icon" role="alert">
              <IconClock />
              <p class="alert__title">{t('fleet.new_task.result_queued')}</p>
              <p class="alert__desc">
                {t('fleet.new_task.result_queued_desc', {
                  position: o().queue_position,
                  depth: o().queue_depth,
                })}
              </p>
            </div>
          )}
        </Match>

        <Match when={of('escalated')}>
          {(o) => (
            <div class="alert alert--icon" role="alert">
              <IconShield />
              <p class="alert__title">{t('fleet.new_task.result_escalated')}</p>
              <p class="alert__desc">
                {t('fleet.new_task.result_escalated_desc', {
                  confidence: o().confidence,
                  threshold: o().threshold,
                })}
              </p>
            </div>
          )}
        </Match>

        <Match when={of('dupe')}>
          {(o) => (
            <div class="alert alert--icon" role="alert">
              <IconLoop />
              <p class="alert__title">{t('fleet.new_task.result_dupe')}</p>
              <p class="alert__desc">{t('fleet.new_task.result_dupe_desc')}</p>
              <Show when={o().linked_pr}>
                {(pr) => (
                  <a
                    class="alert__action"
                    href={`https://github.com/${props.repoFullName}/pull/${pr()}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('fleet.new_task.result_dupe_pr', { number: pr() })}
                  </a>
                )}
              </Show>
            </div>
          )}
        </Match>

        {/* A 2xx that named no task: it may have landed, so neither "dispatched"
            nor "try again" is honest — the board is where the answer is. */}
        <Match when={of('unclear')}>
          <div class="alert alert--icon" role="alert">
            <IconAlertCircle />
            <p class="alert__title">{t('fleet.new_task.result_unclear')}</p>
            <p class="alert__desc">{t('fleet.new_task.result_unclear_desc')}</p>
          </div>
        </Match>

        <Match when={of('refused')}>{(o) => <NewTaskRefused refused={o()} />}</Match>
      </Switch>

      <div class="dialog__footer">
        <Button variant="outline" onClick={props.onAgain}>
          {t('fleet.new_task.another')}
        </Button>
        <Button variant="outline" onClick={props.onClose}>
          {t('fleet.new_task.close')}
        </Button>
        <Show when={openId()}>
          {(id) => (
            <Button onClick={() => props.onOpenTask(id())}>
              {t('fleet.new_task.result_open')}
            </Button>
          )}
        </Show>
      </div>
    </>
  );
}
